import { memo } from "react";
import { Button, Chip, Collapse, Divider, Paper, Stack, Typography } from "@mui/material";
import { ExpandLess, ExpandMore, VideoCall } from "@mui/icons-material";
import type { BookingTarget } from "../../booking/types";
import { PRESENCE_CHIP } from "../../presence/presenceChip";
import type { TeleconsultoriaPresenceStatus } from "../../presence/probeTeleconsultoriaPeer";
import type { TeleconsultorChoice } from "../helpers/teleconsultorChoices";
import { TeleconsultorSlotsPanel } from "./TeleconsultorSlotsPanel";

/**
 * Teleconsultor na marcação: horários no período e presença no mesmo card.
 * Toque no card mostra os horários dele; online + disponível = "Atender agora".
 */
export const TeleconsultorChoiceCard = memo(function TeleconsultorChoiceCard({
  choice,
  presenceStatus,
  isSelected,
  onToggle,
  onBook,
}: {
  choice: TeleconsultorChoice;
  presenceStatus?: TeleconsultoriaPresenceStatus;
  isSelected: boolean;
  onToggle: (key: string) => void;
  onBook: (target: BookingTarget) => void;
}) {
  const slotCount = choice.slots.length;
  const isOnline = choice.isAvailableNow && presenceStatus === "online";
  const presenceChip = choice.isAvailableNow ? PRESENCE_CHIP[presenceStatus ?? "checking"] : undefined;

  return (
    <Paper
      variant="outlined"
      role="listitem"
      sx={{ p: 1.5, borderColor: isSelected ? "primary.main" : undefined }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
        <Stack
          component="button"
          type="button"
          onClick={() => onToggle(choice.key)}
          aria-expanded={isSelected}
          spacing={0.5}
          sx={{
            flex: 1, minWidth: 0, p: 0, border: 0, bgcolor: "transparent",
            textAlign: "left", cursor: "pointer", color: "inherit", font: "inherit",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
              {choice.teleconsultor.display || "Teleconsultor"}
            </Typography>
            {isSelected ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </Stack>
          {choice.serviceName && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {choice.serviceName}
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              color={slotCount ? "primary" : "default"}
              label={slotCount ? `${slotCount} horário${slotCount === 1 ? "" : "s"} livre${slotCount === 1 ? "" : "s"}` : "Sem horários no período"}
            />
            {presenceChip && <Chip size="small" label={presenceChip.label} color={presenceChip.color} />}
          </Stack>
        </Stack>
        {isOnline && (
          <Button
            variant="contained"
            startIcon={<VideoCall />}
            onClick={() => onBook({ mode: "now", teleconsultor: choice.teleconsultor })}
            sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" } }}
          >
            Atender agora
          </Button>
        )}
      </Stack>
      <Collapse in={isSelected} unmountOnExit>
        <Divider sx={{ my: 1.5 }} />
        <TeleconsultorSlotsPanel slots={choice.slots} onBook={onBook} />
      </Collapse>
    </Paper>
  );
});
